'use client'

import { useState } from 'react'

interface MedicoContentProps {
  userEmail?: string | null
  userName?: string | null
  userPhone?: string | null
}

const DDI_OPTIONS = [
  { value: '+55', label: '\u{1F1E7}\u{1F1F7} +55' },
  { value: '+351', label: '\u{1F1F5}\u{1F1F9} +351' },
  { value: '+1', label: '\u{1F1FA}\u{1F1F8} +1' },
]

function formatPhone(value: string): string {
  const d = value.replace(/\D/g, '')
  if (d.length <= 2) return d
  if (d.length <= 7) return '(' + d.slice(0, 2) + ') ' + d.slice(2)
  return '(' + d.slice(0, 2) + ') ' + d.slice(2, 7) + '-' + d.slice(7, 11)
}

export function MedicoContent({ userEmail, userName, userPhone }: MedicoContentProps) {
  const [name, setName] = useState(userName || '')
  const [email, setEmail] = useState(userEmail || '')
  const [ddi, setDdi] = useState('+55')
  const [phone, setPhone] = useState(userPhone ? userPhone.replace(/^\+55/, '') : '')
  const [specialty, setSpecialty] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim() || !email.trim()) {
      setError('Preencha nome e e-mail.')
      return
    }
    setLoading(true)
    setError('')

    try {
      const res = await fetch('/api/lead-medico', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          phone: ddi + phone.replace(/\D/g, ''),
          specialty: specialty.trim(),
          source: 'medico-landing',
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || 'Erro ao registrar. Tente novamente.')
      }
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro inesperado.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Barra de urgência */}
      <div className="bg-emerald-700 text-white text-center py-2 px-3 text-xs sm:text-sm font-medium">
        Evento ao vivo e gratuito para profissionais da medicina. Vagas limitadas.
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-gray-950 via-emerald-950 to-gray-950">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-800/30 via-transparent to-transparent" />
        <div className="relative max-w-5xl mx-auto px-4 pt-10 pb-12 sm:pt-14 sm:pb-16 md:pt-16 md:pb-20">
          <div className="grid md:grid-cols-2 gap-8 md:gap-10 items-center">
            {/* Copy */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-full text-[11px] sm:text-xs font-semibold mb-4 sm:mb-6 uppercase tracking-wide">
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>
                Evento exclusivo para médicos
              </div>

              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-5 leading-tight">
                Seu conhecimento clínico salva vidas.{' '}
                <span className="text-emerald-400">E se ele também formasse milhares de profissionais?</span>
              </h1>

              <p className="text-base sm:text-lg text-emerald-100/80 mb-5 sm:mb-6 leading-relaxed">
                Descubra como médicos e professores de medicina estão transformando experiência clínica em livros e produtos educacionais de alto impacto, com IA e 6 décadas de ciência pedagógica.
              </p>

              <div className="flex flex-wrap gap-2 sm:gap-3 text-xs sm:text-sm">
                <span className="bg-white/5 border border-white/10 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-emerald-200">12 pesquisadores</span>
                <span className="bg-white/5 border border-white/10 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-emerald-200">6 décadas de ciência</span>
                <span className="bg-white/5 border border-white/10 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-emerald-200">IA que extrai conhecimento tácito</span>
              </div>
            </div>

            {/* Form */}
            <div className="bg-white rounded-2xl shadow-2xl shadow-black/40 p-5 sm:p-6 md:p-8">
              {success ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Inscrição confirmada!</h3>
                  <p className="text-gray-600 mb-4">
                    Enviamos os detalhes do evento para <strong>{email}</strong>.
                  </p>
                  <p className="text-sm text-gray-500">Fique atento ao seu e-mail e WhatsApp. Nos vemos no evento!</p>
                </div>
              ) : (
                <>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">Garanta sua vaga gratuita</h3>
                  <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-5">Preencha abaixo e receba o link de acesso exclusivo.</p>

                  <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Como podemos te chamar?</label>
                      <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Dr(a). Nome Completo"
                        className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-200 rounded-xl text-gray-900 text-sm sm:text-base placeholder:text-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" required />
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Seu melhor e-mail</label>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@exemplo.com.br"
                        className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-200 rounded-xl text-gray-900 text-sm sm:text-base placeholder:text-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" required />
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
                      <div className="flex gap-2">
                        <select value={ddi} onChange={e => setDdi(e.target.value)}
                          className="w-24 sm:w-28 px-2 sm:px-3 py-2.5 sm:py-3 border border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition">
                          {DDI_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <input type="tel" value={phone} onChange={e => setPhone(formatPhone(e.target.value))} placeholder="(61) 99999-9999"
                          className="flex-1 min-w-0 px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-200 rounded-xl text-gray-900 text-sm sm:text-base placeholder:text-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Especialidade</label>
                      <select value={specialty} onChange={e => setSpecialty(e.target.value)}
                        className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-200 rounded-xl text-gray-900 text-sm sm:text-base focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition">
                        <option value="">Selecione sua área</option>
                        <option value="clinica-medica">Clínica Médica</option>
                        <option value="cirurgia">Cirurgia</option>
                        <option value="pediatria">Pediatria</option>
                        <option value="ginecologia">Ginecologia e Obstetrícia</option>
                        <option value="cardiologia">Cardiologia</option>
                        <option value="ortopedia">Ortopedia</option>
                        <option value="dermatologia">Dermatologia</option>
                        <option value="neurologia">Neurologia</option>
                        <option value="psiquiatria">Psiquiatria</option>
                        <option value="anestesiologia">Anestesiologia</option>
                        <option value="radiologia">Radiologia</option>
                        <option value="medicina-familia">Medicina de Família</option>
                        <option value="urgencia">Urgência e Emergência</option>
                        <option value="preceptoria">Preceptoria / Docência</option>
                        <option value="outra">Outra especialidade</option>
                      </select>
                    </div>

                    {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

                    <button type="submit" disabled={loading}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 sm:py-4 px-6 rounded-xl text-base sm:text-lg transition disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/20">
                      {loading ? 'Registrando...' : 'QUERO MINHA VAGA GRATUITA'}
                    </button>
                  </form>

                  <p className="text-xs text-gray-400 mt-3 text-center">
                    Seus dados estão seguros. Não enviamos spam.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Dores */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2 sm:mb-3">
            Você se reconhece em alguma dessas situações?
          </h2>
          <p className="text-sm sm:text-base text-gray-500 text-center mb-8 sm:mb-12 max-w-2xl mx-auto">Praticamente todo médico que acumulou experiência clínica relevante já se viu preso nessas armadilhas.</p>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            {[
              {
                icon: (
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                ),
                title: 'Conhecimento de sobra. Tempo de sobra? Nunca.',
                text: 'Você acumulou anos de residência, plantões, casos raros e decisões difíceis. Tem um livro inteiro na cabeça. Mas entre consultório, preceptoria e vida pessoal, sentar para escrever parece impossível.',
              },
              {
                icon: (
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                ),
                title: 'A academia exige formato. O mercado exige velocidade.',
                text: 'Publicar por editora acadêmica leva meses, às vezes anos. Enquanto isso, profissionais menos experientes já estão ensinando online. Seu conhecimento merece chegar mais longe, mais rápido.',
              },
              {
                icon: (
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                ),
                title: 'Saber fazer não é saber ensinar.',
                text: 'Você diagnostica e decide com intuição clínica afiada por milhares de horas de prática. Mas quando tenta explicar COMO faz, as palavras não saem. Isso tem nome na ciência: conhecimento tácito. E existe método para extraí-lo.',
              },
              {
                icon: (
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" /></svg>
                ),
                title: 'Tentou criar um curso e travou na estrutura.',
                text: 'Gravou aulas ou escreveu capítulos soltos, mas faltou fio condutor. O aluno não aprende porque o conteúdo não tem sequência lógica nem respeita a carga cognitiva. Resultado: material incompleto na gaveta.',
              },
            ].map((dor, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-100 shadow-sm hover:shadow-md transition">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center mb-3 sm:mb-4">
                  {dor.icon}
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1.5 sm:mb-2">{dor.title}</h3>
                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">{dor.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* O que vai descobrir */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2 sm:mb-3">
            No evento, você vai descobrir
          </h2>
          <p className="text-sm sm:text-base text-gray-500 text-center mb-8 sm:mb-12">O caminho completo de expert com conhecimento na cabeça a autor de um produto educacional estruturado.</p>

          <div className="space-y-3 sm:space-y-5">
            {[
              {
                num: '01',
                title: 'Como extrair conhecimento tácito da prática clínica',
                text: 'Usando o mesmo princípio que Michael Polanyi descreveu e que a IA agora consegue operacionalizar em conversas guiadas. Você vai articular o que sempre soube fazer, mas nunca conseguiu escrever.',
              },
              {
                num: '02',
                title: 'O método de "design reverso" usado em Harvard e Johns Hopkins',
                text: 'Wiggins e McTighe criaram o framework que os melhores currículos médicos do mundo usam: começar pelo resultado desejado do aluno e construir o caminho de trás para frente.',
              },
              {
                num: '03',
                title: 'Por que 87% dos cursos online falham nos primeiros 30 minutos',
                text: 'E como os 9 eventos instrucionais de Gagné resolvem isso ao respeitar o funcionamento real do cérebro adulto durante a aprendizagem.',
              },
              {
                num: '04',
                title: 'A ciência por trás do esquecimento na preceptoria',
                text: 'A Teoria da Carga Cognitiva de Sweller explica por que seus residentes esquecem o que você ensina, e o que fazer de diferente para que retenham.',
              },
              {
                num: '05',
                title: 'De expert a autor em semanas, não em anos',
                text: 'O passo a passo para ir de "conhecimento na cabeça" a "produto educacional estruturado", com fluxos de IA que fazem o trabalho pesado de organização.',
              },
            ].map((item) => (
              <div key={item.num} className="flex gap-3 sm:gap-5 items-start p-4 sm:p-5 rounded-2xl hover:bg-gray-50 transition">
                <div className="text-2xl sm:text-3xl font-black text-emerald-200 shrink-0 w-9 sm:w-12">{item.num}</div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">{item.title}</h3>
                  <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Metodologia / Ciência */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gradient-to-b from-gray-950 to-emerald-950 text-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3">
              Isso não é mais um &quot;método de guru&quot;. É ciência aplicada.
            </h2>
            <p className="text-sm sm:text-base text-emerald-200/80 max-w-2xl mx-auto">
              A Metodologia APE sintetiza o trabalho de 12 pesquisadores ao longo de 6 décadas. Não inventamos nada. Organizamos o que a ciência já provou que funciona.
            </p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 sm:p-8 mb-8 sm:mb-10">
            <p className="text-center text-base sm:text-lg font-medium text-emerald-300 italic mb-0">
              &quot;Você confia em medicina baseada em evidências para tratar seus pacientes. Por que usaria algo diferente para criar seus produtos educacionais?&quot;
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {[
              { name: 'Polanyi e Klein', desc: 'Extração de conhecimento tácito e tomada de decisão expert' },
              { name: 'Knowles', desc: 'Andragogia: como adultos realmente aprendem' },
              { name: 'Wiggins e McTighe', desc: 'Design reverso: comece pelo resultado, não pelo conteúdo' },
              { name: 'Gagné', desc: 'Os 9 eventos instrucionais que garantem retenção real' },
              { name: 'Sweller', desc: 'Carga cognitiva: menos é mais ao ensinar procedimentos complexos' },
              { name: 'Ericsson', desc: 'Prática deliberada: o mesmo princípio da formação de cirurgiões expert' },
            ].map((r) => (
              <div key={r.name} className="bg-white/5 border border-white/10 rounded-xl p-3 sm:p-4 hover:border-emerald-500/30 transition">
                <h4 className="font-bold text-emerald-300 text-sm sm:text-base mb-1">{r.name}</h4>
                <p className="text-xs sm:text-sm text-gray-300">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Para quem é */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 text-center mb-6 sm:mb-10">
            Este evento foi desenhado para quem:
          </h2>

          <div className="space-y-3 sm:space-y-4">
            {[
              'É médico, residente ou professor de medicina e sente que tem conhecimento clínico valioso que não está chegando a quem precisa',
              'Já tentou escrever um livro, criar um curso ou organizar seu método de ensino, mas travou na estrutura',
              'Quer criar produtos educacionais sem depender de editoras acadêmicas ou de "fórmulas de lançamento"',
              'Valoriza ciência e método. Não quer atalhos. Quer um processo que funcione e respeite sua inteligência',
              'Tem pouco tempo disponível e precisa de um sistema eficiente que extraia e organize conhecimento',
            ].map((item, i) => (
              <div key={i} className="flex gap-3 sm:gap-4 items-start bg-white rounded-xl p-4 sm:p-5 border border-gray-100">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                </div>
                <p className="text-sm sm:text-base text-gray-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
            Seu conhecimento clínico já salvou vidas. Agora ele pode formar quem vai salvar as próximas.
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
            Inscreva-se gratuitamente e descubra como transformar décadas de experiência médica em produtos educacionais que ensinam de verdade.
          </p>

          {success ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8">
              <div className="text-emerald-600 font-bold text-lg mb-2">Você já está inscrito!</div>
              <p className="text-gray-600 text-sm">Verifique seu e-mail ({email}) para os detalhes do evento.</p>
            </div>
          ) : (
            <>
              <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 sm:py-4 px-8 sm:px-10 rounded-xl text-base sm:text-lg transition shadow-lg shadow-emerald-500/20">
                QUERO MINHA VAGA GRATUITA
              </button>
              <p className="text-xs sm:text-sm text-gray-400 mt-3 sm:mt-4">Vagas limitadas. Evento ao vivo sem reprise.</p>
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 sm:py-8 px-4 bg-gray-950 text-center">
        <p className="text-xs sm:text-sm text-gray-500">
          Metodologia APE - Baseada em 12 pesquisadores, 6 décadas de ciência educacional.
        </p>
        <p className="text-xs text-gray-600 mt-1">eBuz Negócios Digitais LTDA - CNPJ 29.436.457/0001-25</p>
      </footer>
    </div>
  )
}
